// src/modules/communications/context/CommunicationContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { sendSms, initiateClickToCall } from '../services/communicationService';

// Create context
const CommunicationContext = createContext();

// Custom hook to use the communication context
export const useCommunication = () => {
  const context = useContext(CommunicationContext);
  if (!context) {
    throw new Error('useCommunication must be used within a CommunicationProvider');
  }
  return context;
};

export const CommunicationProvider = ({ children }) => {
  // SMS composer state
  const [smsComposerOpen, setSmsComposerOpen] = useState(false);
  const [smsRecipient, setSmsRecipient] = useState({ phone: '', leadId: null, name: '' });

  // Call modal state
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callTarget, setCallTarget] = useState({ phone: '', leadId: null, name: '' });
  
  // Toast notifications state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // SMS functions
  const openSmsComposer = useCallback((phone, leadId, name = '') => {
    setSmsRecipient({ phone, leadId, name });
    setSmsComposerOpen(true);
  }, []);

  const closeSmsComposer = useCallback(() => {
    setSmsComposerOpen(false);
  }, []);

  const sendSmsMessage = useCallback(async (message, mediaFile = null) => {
    try {
      const result = await sendSms(
        smsRecipient.phone,
        message,
        smsRecipient.leadId,
        mediaFile
      );
      
      setNotification({
        show: true,
        message: 'Message sent successfully',
        type: 'success'
      });
      
      return result;
    } catch (error) {
      setNotification({
        show: true,
        message: `Failed to send message: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
      
      throw error;
    }
  }, [smsRecipient]);

  // Call functions
  const openCallModal = useCallback((phone, leadId, name = '') => {
    setCallTarget({ phone, leadId, name });
    setCallModalOpen(true);
  }, []);

  const closeCallModal = useCallback(() => {
    setCallModalOpen(false);
  }, []);

  const initiateCall = useCallback(async (userPhone, notes = '') => {
    try {
      const result = await initiateClickToCall({
        to_number: callTarget.phone,
        callback_number: userPhone,
        lead_id: callTarget.leadId,
        notes
      });
      
      setNotification({
        show: true,
        message: 'Call initiated! Your phone will ring momentarily.',
        type: 'success'
      });
      
      return result;
    } catch (error) {
      setNotification({
        show: true,
        message: `Failed to initiate call: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
      
      throw error;
    }
  }, [callTarget]);

  // Notification functions
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ show: true, message, type });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, show: false }));
  }, []);

  // Context value
  const value = {
    // SMS
    smsComposerOpen,
    smsRecipient,
    openSmsComposer,
    closeSmsComposer,
    sendSmsMessage,
    
    // Call
    callModalOpen,
    callTarget,
    openCallModal,
    closeCallModal,
    initiateCall,
    
    // Notifications
    notification,
    showNotification,
    hideNotification
  };

  return (
    <CommunicationContext.Provider value={value}>
      {children}
    </CommunicationContext.Provider>
  );
};